/**
 * Field-smoothing pre-filter for the "Mosaic GL" lab (mosaicGlController.ts,
 * mosaicSmoothWorker.ts) — the CPU FALLBACK path, off the main thread, on
 * the raw decoded grid BEFORE it ever reaches the display shader
 * (mosaicGlShader.ts does none of this; it only ever resamples whatever
 * byte grid is currently uploaded, raw or pre-smoothed, with the exact same
 * nodata-aware kernel either way — see mosaicGlController.ts's doc comment
 * on why that reuse is safe). Two independent stages, per the study's
 * validated round-3 spec (supersedes an earlier ratio/coverage-channel
 * design from round 2 — see git history if that's ever needed again):
 *
 *  1. despeckle(): drops isolated single/sparse-cell echo speckle to nodata
 *     BEFORE anything else touches the grid.
 *  2. fieldSmooth()'s blur stage: maps every no-echo cell (raw === 0, in- or
 *     out-of-coverage alike — see its own doc comment on that known caveat)
 *     to a real LOW value (0 dBZ, not "missing"), then blurs that field —
 *     no ratio, no coverage channel, kernel sums to 1. This is deliberately
 *     NOT nodata-aware (round 2's ratio-based version was — reverted
 *     because renormalizing around missing data produced spiky, artifact-
 *     prone floor contours); the honest cost is that a real echo cell
 *     directly adjacent to a no-echo cell IS measurably pulled toward that
 *     low value, same as any plain blur — this is intentional, not a bug
 *     (mosaicGlFieldSmoothing.test.ts asserts exactly this, the opposite of
 *     what a nodata-aware blur would do).
 *
 * PRIMARY vs FALLBACK PATH (added after this file's own perf pass):
 * mosaicGlCompositor.ts now runs the SAME two stages on the GPU (a
 * fragment-shader despeckle pass + separable blur via FBO ping-pong,
 * mosaicGlSmoothShader.ts) whenever the browser/driver supports a
 * renderable float/half-float target (mosaicGlCapabilities.ts) — that's
 * the PRIMARY path now, effectively free per frame. This file's despeckle/
 * fieldSmooth/mosaicSmoothWorker.ts remain the FALLBACK for when GPU
 * smoothing capability isn't available, unchanged and still exercised by
 * every test below. EXACTNESS BONUS: the GPU path runs the TRUE exact
 * Gaussian (gaussianKernel1D's own weights, uploaded as shader uniforms —
 * no approximation needed once it's cheap enough to run every frame). The
 * box-blur approximation (approximateGaussianBlur, below) and its up-to-
 * ~6 dBZ local deviation from the exact Gaussian exist ONLY to keep the CPU
 * fallback path fast; a GPU-capable browser never sees that tradeoff at all.
 *
 * `sigma === 0` ("off") is a byte-identical passthrough for the blur stage —
 * despeckle is independently toggled and runs regardless of sigma.
 */
import { PRECIP_FLOOR_RAW } from "./fusionCompositor";
import { dbzToValue } from "./palette";

/** A cell counts as "echo" for despeckle purposes at the same >=10 dBZ
 * threshold the rest of this client already uses for "precip only"
 * (fusionCompositor.ts's PRECIP_FLOOR_RAW) — reused, not re-derived. */
export const DESPECKLE_ECHO_FLOOR_RAW = PRECIP_FLOOR_RAW;
/** Fewer than this many echo neighbors (of up to 8, in the 3x3 neighborhood)
 * and an echo cell is dropped to nodata. */
export const DESPECKLE_MIN_ECHO_NEIGHBORS = 3;

/** The raw byte for exactly 0 dBZ, via palette.ts's own dbzToValue (the
 * single source of truth for this client's dBZ<->raw-byte quantization
 * contract) — NOT a hand-typed literal, so this stays correct if that
 * contract ever changes. (65, not the round-numbered "66" a quick mental
 * approximation might suggest — palette.ts's real quantization is
 * `dBZ = (raw-1)/2 - 32`, not the simpler `raw*0.5 - 33` shorthand.) */
export const NO_ECHO_FILL_RAW = dbzToValue(0);

/** The four sigma settings the lab panel offers, in grid cells; 0 means
 * "off" (blur stage is a passthrough). */
export const SIGMA_LEVELS: readonly number[] = [0, 0.75, 1, 1.6];

/** Drops echo cells (raw >= `echoFloorRaw`, default the 10 dBZ "precip only"
 * threshold) with fewer than `DESPECKLE_MIN_ECHO_NEIGHBORS` echo neighbors
 * in their 3x3 neighborhood to nodata (raw = 0). Out-of-bounds neighbors
 * (grid edges) simply aren't counted — an edge cell only ever has as many
 * neighbors as actually exist, never treated as either echo or non-echo by
 * assumption. Non-echo cells are never touched. Returns a new array; `raw`
 * is never mutated. */
export function despeckle(raw: Uint8Array, width: number, height: number, echoFloorRaw: number = DESPECKLE_ECHO_FLOOR_RAW): Uint8Array {
  const out = new Uint8Array(raw);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * width + x;
      if (raw[i] < echoFloorRaw) continue; // only echo cells are ever despeckled
      let echoNeighbors = 0;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0) continue;
          const nx = x + dx;
          const ny = y + dy;
          if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue; // edge: not counted either way
          if (raw[ny * width + nx] >= echoFloorRaw) echoNeighbors++;
        }
      }
      if (echoNeighbors < DESPECKLE_MIN_ECHO_NEIGHBORS) out[i] = 0;
    }
  }
  return out;
}

/** Maps every no-echo cell (raw === 0) to `fillRaw` (default
 * NO_ECHO_FILL_RAW, 0 dBZ) — a real, low measurement value, not "missing" —
 * so the blur stage below never needs a nodata/coverage concept at all.
 * KNOWN CAVEAT (documented, accepted for the lab): this can't distinguish
 * "in-coverage, genuinely no echo" from "outside all radar coverage
 * entirely" — both are raw === 0 at this point in the pipeline — so smoothed
 * echoes near the outer edge of total radar coverage fade artificially
 * toward this fill value. A future fix would need a separate coverage mask;
 * out of scope here. */
export function mapNoEchoAsLow(raw: Uint8Array, fillRaw: number = NO_ECHO_FILL_RAW): Float32Array {
  const out = new Float32Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw[i] > 0 ? raw[i] : fillRaw;
  return out;
}

/** Normalized (sums to 1) 1D Gaussian kernel, radius = ceil(3*sigma) per the
 * spec, minimum radius 1 so a very small sigma still blurs something rather
 * than degenerating to a single-tap no-op. */
export function gaussianKernel1D(sigma: number): Float32Array {
  const radius = Math.max(1, Math.ceil(3 * sigma));
  const size = radius * 2 + 1;
  const kernel = new Float32Array(size);
  let sum = 0;
  for (let i = 0; i < size; i++) {
    const x = i - radius;
    const w = Math.exp(-(x * x) / (2 * sigma * sigma));
    kernel[i] = w;
    sum += w;
  }
  for (let i = 0; i < size; i++) kernel[i] /= sum;
  return kernel;
}

/** The largest radius any offered sigma level (SIGMA_LEVELS) actually
 * needs: ceil(3*1.6) = 5. mosaicGlCompositor.ts's GPU blur pass is a FIXED
 * 11-tap (2*5+1) shader — one compiled program reused for every sigma,
 * with weights re-uploaded as uniforms on change (mosaicGlSmoothShader.ts)
 * — rather than compiling a different-sized shader per sigma level. */
export const MAX_BLUR_RADIUS = 5;
export const BLUR_TAP_COUNT = MAX_BLUR_RADIUS * 2 + 1;

/** Centers `kernel` (gaussianKernel1D(sigma)'s own, variable-length, EXACT
 * output) into a fixed BLUR_TAP_COUNT-length array, zero-padded evenly on
 * both sides — a smaller sigma's shorter natural kernel (e.g. sigma=0.75 or
 * 1, radius 3, 7 taps) just gets zero weight on the outer two taps on each
 * side of the fixed 11-tap shader. Throws if `kernel` is longer than
 * BLUR_TAP_COUNT — that would mean some sigma now needs a bigger radius
 * than the GPU shader supports, a real bug (a new, larger SIGMA_LEVELS
 * entry added without updating MAX_BLUR_RADIUS/the shader) to catch
 * immediately rather than silently truncate weights and corrupt the blur. */
export function padKernelToBlurTapCount(kernel: Float32Array): Float32Array {
  if (kernel.length > BLUR_TAP_COUNT) {
    throw new Error(`kernel length ${kernel.length} exceeds BLUR_TAP_COUNT ${BLUR_TAP_COUNT} — a sigma now needs a bigger GPU blur radius`);
  }
  const padded = new Float32Array(BLUR_TAP_COUNT);
  const offset = (BLUR_TAP_COUNT - kernel.length) / 2; // always an integer: both lengths are odd
  padded.set(kernel, offset);
  return padded;
}

function clampInt(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Plain separable Gaussian blur over `field` (already no-echo-as-low
 * mapped) — NOT nodata-aware (see this file's top doc comment on why round
 * 2's ratio/coverage-channel version was reverted): no ratio, no coverage
 * channel, just two 1D convolution passes whose kernel sums to 1.
 * Edge-clamped (out-of-bounds taps read the nearest in-bounds cell), same
 * convention as the GL texture's own CLAMP_TO_EDGE wrap. `sigma <= 0` is a
 * byte-identical passthrough (returns `field` itself, unblurred). */
export function separableGaussianBlur(field: Float32Array, width: number, height: number, sigma: number): Float32Array {
  if (sigma <= 0) return field;
  const kernel = gaussianKernel1D(sigma);
  const radius = (kernel.length - 1) / 2;
  const tmp = new Float32Array(width * height);
  const out = new Float32Array(width * height);

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let acc = 0;
      for (let k = -radius; k <= radius; k++) {
        const sx = clampInt(x + k, 0, width - 1);
        acc += field[y * width + sx] * kernel[k + radius];
      }
      tmp[y * width + x] = acc;
    }
  }
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let acc = 0;
      for (let k = -radius; k <= radius; k++) {
        const sy = clampInt(y + k, 0, height - 1);
        acc += tmp[sy * width + x] * kernel[k + radius];
      }
      out[y * width + x] = acc;
    }
  }
  return out;
}

/**
 * PERFORMANCE: `separableGaussianBlur` above is O(width*height*radius) —
 * measured (client-side benchmark against a real production mosaic
 * fixture, dev-fixtures/mosaic/conus/) at 550-820ms for a single IFD0
 * (5888x3328) frame at sigma 0.75-1.6, and still 140-210ms at IFD1
 * (2944x1664). That's the dominant cost in the entire scrub pipeline by a
 * wide margin (despeckle: ~32ms at IFD0; colorize: ~23-100ms) — nowhere
 * close to "instant" scrubbing. `approximateGaussianBlur` below replaces it
 * as the pipeline's actual blur (fieldSmooth calls this one, not the exact
 * version) with the standard "three box blurs approximate a Gaussian"
 * technique (the same one browsers' own CSS `filter: blur()` uses for
 * exactly this reason): each box blur pass is O(width*height) REGARDLESS
 * OF RADIUS via a sliding-window running sum (add the incoming tap, remove
 * the outgoing one — algebraically exact for a box kernel, clamped edges
 * included, since the removed/added terms telescope regardless of how
 * clamping happens to fold multiple offsets onto the same edge index), so
 * three passes total cost O(6*width*height) independent of sigma — versus
 * the exact version's O(4*width*height*radius), which grows with sigma.
 *
 * `separableGaussianBlur`/`gaussianKernel1D` stay exported: they're the
 * reference `approximateGaussianBlur` is verified against (mosaicGlFieldSmoothing.
 * test.ts's tolerance test) and remain independently useful/tested pure math.
 */

/** Ivan Kutskir's "boxesForGauss" — the standard closed-form box-blur widths
 * that, applied as `passes` successive box-blur convolutions, approximate a
 * true Gaussian of the given `sigma` (variance-matched: n box passes of
 * widths w_i have combined variance sum((w_i^2-1)/12), solved for widths
 * that split as evenly as integer box widths allow). Returns RADII (not
 * widths) in application order. Widely used in production (this exact
 * formula backs several browser/graphics-library fast-blur implementations)
 * — not derived from scratch here, just implemented from the well-known
 * closed form. */
export function boxRadiiForGauss(sigma: number, passes = 3): number[] {
  const n = passes;
  const wIdeal = Math.sqrt((12 * sigma * sigma) / n + 1);
  let wl = Math.floor(wIdeal);
  if (wl % 2 === 0) wl -= 1; // widths must be odd (a box of width 2r+1)
  wl = Math.max(1, wl);
  const wu = wl + 2;

  const mIdeal = (12 * sigma * sigma - n * wl * wl - 4 * n * wl - 3 * n) / (-4 * wl - 4);
  const m = Math.round(mIdeal);

  const radii: number[] = [];
  for (let i = 0; i < n; i++) {
    const width = i < m ? wl : wu;
    radii.push((width - 1) / 2);
  }
  return radii;
}

/** One box-blur pass (uniform average over a `2*radius+1` window), edge-
 * clamped, via a sliding-window running sum — O(width*height) regardless of
 * `radius`. Separable: a full pass does horizontal-then-vertical, matching
 * `separableGaussianBlur`'s own two-pass structure/edge convention exactly
 * (CLAMP_TO_EDGE, same as the GL texture's own wrap mode). `radius <= 0` is
 * a passthrough (a box of width 1 is the identity). */
export function boxBlurPass(field: Float32Array, width: number, height: number, radius: number): Float32Array {
  if (radius <= 0) return field;
  const size = 2 * radius + 1;
  const invSize = 1 / size; // precomputed once — a per-pixel multiply beats a per-pixel division
  const tmp = new Float32Array(width * height);
  const out = new Float32Array(width * height);

  for (let y = 0; y < height; y++) {
    const rowOff = y * width;
    let sum = 0;
    for (let k = -radius; k <= radius; k++) sum += field[rowOff + clampInt(k, 0, width - 1)];
    tmp[rowOff] = sum * invSize;
    for (let x = 1; x < width; x++) {
      const addIdx = clampInt(x + radius, 0, width - 1);
      const remIdx = clampInt(x - radius - 1, 0, width - 1);
      sum += field[rowOff + addIdx] - field[rowOff + remIdx];
      tmp[rowOff + x] = sum * invSize;
    }
  }
  for (let x = 0; x < width; x++) {
    let sum = 0;
    for (let k = -radius; k <= radius; k++) sum += tmp[clampInt(k, 0, height - 1) * width + x];
    out[x] = sum * invSize;
    for (let y = 1; y < height; y++) {
      const addIdx = clampInt(y + radius, 0, height - 1);
      const remIdx = clampInt(y - radius - 1, 0, height - 1);
      sum += tmp[addIdx * width + x] - tmp[remIdx * width + x];
      out[y * width + x] = sum * invSize;
    }
  }
  return out;
}

/** The actual runtime blur `fieldSmooth` calls — three successive
 * `boxBlurPass` convolutions at the radii `boxRadiiForGauss` derives for
 * `sigma`, approximating `separableGaussianBlur(field, width, height, sigma)`
 * at a fraction of the cost (see this file's PERFORMANCE doc comment above).
 * `sigma <= 0` is a byte-identical passthrough, same as the exact version. */
export function approximateGaussianBlur(field: Float32Array, width: number, height: number, sigma: number): Float32Array {
  if (sigma <= 0) return field;
  let current = field;
  for (const radius of boxRadiiForGauss(sigma)) current = boxBlurPass(current, width, height, radius);
  return current;
}

export interface FieldSmoothOptions {
  despeckle: boolean;
  sigma: number;
}

/** The full field-smoothing pipeline mosaicSmoothWorker.ts runs per frame
 * per LOD: despeckle (independently toggled) -> no-echo-as-low ->
 * approximateGaussianBlur (skipped entirely when `sigma <= 0`) ->
 * rounded/clamped back to a u8 raw-byte grid, the same texture format the
 * GL path already uploads. `despeckle: false, sigma: 0` (both toggles off,
 * the default) is a byte-identical passthrough of `raw` — asserted
 * directly in mosaicGlFieldSmoothing.test.ts, not just implied. */
export function fieldSmooth(raw: Uint8Array, width: number, height: number, opts: FieldSmoothOptions): Uint8Array {
  const despeckled = opts.despeckle ? despeckle(raw, width, height) : raw;
  if (opts.sigma <= 0) return despeckled;

  const field = mapNoEchoAsLow(despeckled);
  const blurred = approximateGaussianBlur(field, width, height, opts.sigma);
  const out = new Uint8Array(width * height);
  for (let i = 0; i < blurred.length; i++) out[i] = clampInt(Math.round(blurred[i]), 0, 255);
  return out;
}
